<div class="analytics">
  <header class="analytics__brand-bar">
    <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
  </header>
  <section class="analytics__container">
    @if (loading()) {
      <div class="analytics__loading">
        <p>Loading analytics…</p>
      </div>
    } @else {
      <header class="analytics__header">
        <div>
          <h1 class="analytics__title">
            <span>Welcome Back,</span>
            <span>{{ doctorName() }}</span>
          </h1>
          <p class="analytics__subtitle">Your visit analytics for neurological practice</p>
        </div>
      </header>

      <form class="analytics__search" (submit)="$event.preventDefault(); onSearch()">
        <input
          class="analytics__search-input"
          type="search"
          placeholder="Search by patient name, MRN, or visit status…"
          [ngModel]="searchText()"
          (ngModelChange)="searchText.set($event)"
          name="analyticsSearch" />
        <div class="analytics__status-wrap" (click)="$event.stopPropagation()">
          <button
            type="button"
            class="analytics__search-status"
            [attr.aria-expanded]="statusOpen()"
            aria-haspopup="listbox"
            (click)="toggleStatusMenu($event)">
            <span>{{ selectedStatus() }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          @if (statusOpen()) {
            <ul class="analytics__status-menu" role="listbox">
              @for (option of statusOptions; track option.value) {
                <li role="none">
                  <button
                    type="button"
                    role="option"
                    class="analytics__status-option"
                    [class.is-selected]="selectedStatus() === option.value"
                    (click)="chooseStatus(option.value, $event)">
                    {{ option.label }}
                  </button>
                </li>
              }
            </ul>
          }
        </div>
        <button type="submit" class="analytics__search-btn">Search visits</button>
      </form>

      @if (error()) {
        <p class="analytics__error">Error loading metrics. Please try again.</p>
      } @else if (!transcriptions().length) {
        <div class="analytics__empty">
          <p>No statistics exist yet.</p>
          <button type="button" class="analytics__link-btn" (click)="onNewVisit()">Record a conversation</button>
        </div>
      } @else {
        <div class="analytics__metrics">
          <article class="analytics__metric analytics__metric--review">
            <div class="analytics__metric-head">
              <h2>Needs Review</h2>
              <span class="analytics__metric-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
              </span>
            </div>
            <p class="analytics__metric-number">{{ keyMetrics().needsReview }}</p>
            <p class="analytics__metric-copy">SOAP notes awaiting review before EMR submission</p>
          </article>

          <article class="analytics__metric analytics__metric--progress">
            <div class="analytics__metric-head">
              <h2>In Progress</h2>
              <span class="analytics__metric-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
              </span>
            </div>
            <p class="analytics__metric-number">{{ keyMetrics().inProgress }}</p>
            <p class="analytics__metric-copy">Visits currently being processed</p>
          </article>

          <article class="analytics__metric analytics__metric--done">
            <div class="analytics__metric-head">
              <h2>Completed</h2>
              <span class="analytics__metric-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <path d="M22 4 12 14.01l-3-3" />
                </svg>
              </span>
            </div>
            <p class="analytics__metric-number">{{ keyMetrics().completed }}</p>
            <p class="analytics__metric-copy">Visits already completed</p>
          </article>
        </div>

        <div class="analytics__summaries">
          <article class="analytics__summary">
            <div>
              <p class="analytics__summary-label">Total visits</p>
              <p class="analytics__summary-number">{{ summary().total }}</p>
            </div>
          </article>
          <article class="analytics__summary">
            <div>
              <p class="analytics__summary-label">Patients</p>
              <p class="analytics__summary-number">{{ summary().patients }}</p>
            </div>
          </article>
          <article class="analytics__summary">
            <div>
              <p class="analytics__summary-label">Hours recorded</p>
              <p class="analytics__summary-number">{{ summary().hours }}</p>
            </div>
          </article>
          <article class="analytics__summary">
            <div>
              <p class="analytics__summary-label">Completion rate</p>
              <p class="analytics__summary-number">{{ summary().completionRate }}%</p>
            </div>
          </article>
        </div>
      }

      <section class="analytics__recent">
        <h2 class="analytics__recent-title">Recent visits</h2>

        @if (!transcriptions().length) {
          <p class="analytics__muted">No visits found.</p>
        } @else {
          <div class="analytics__recent-list">
            @for (visit of recentVisits(); track visit.id) {
              <article
                class="analytics__visit"
                [class.analytics__visit--locked]="isInProgress(visit)">
                <div class="analytics__visit-top">
                  <h3>{{ visitTitle(visit) }}</h3>
                  <button
                    type="button"
                    class="analytics__visit-action"
                    [disabled]="isInProgress(visit)"
                    (click)="onRecentVisitAction(visit)">
                    {{ recentActionLabel(visit) }}
                  </button>
                </div>
                <p class="analytics__visit-patient">
                  Patient:
                  <a href="#" (click)="onPatient(visit, $event)">{{ visit.patientName || 'Unknown' }}</a>
                  <span> (MRN: {{ visit.mrn || 'N/A' }})</span>
                </p>
                <div class="analytics__visit-meta">
                  <span>Date: {{ formatDate(visit.date) }}</span>
                  <span class="analytics__status" [ngClass]="statusClass(visit.status)">
                    {{ displayStatus(visit.status) }}
                  </span>
                </div>
                <p class="analytics__visit-duration">Duration: {{ visit.duration || 'N/A' }}</p>
              </article>
            }
          </div>

          <div class="analytics__recent-footer">
            <button type="button" class="analytics__view-all" (click)="onViewAllVisits()">
              View all visits
            </button>
          </div>
        }
      </section>
    }
  </section>
</div>
